import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFeatureFlagControls, useFeatureFlags } from '@/config/featureFlags';
import {
  FEATURE_FLAGS,
  FEATURE_FLAG_CATEGORIES,
} from '@/config/featureFlags/constants';

interface FeatureFlagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FeatureFlagsByCategory {
  [category: string]: Array<{
    key: string;
    description: string;
    enabled: boolean;
  }>;
}

export const FeatureFlagsDialog = ({
  open,
  onOpenChange,
}: FeatureFlagsDialogProps) => {
  const currentFlags = useFeatureFlags();
  const { setFlag, resetAllFlags } = useFeatureFlagControls();
  const [localFlags, setLocalFlags] = useState<Record<string, boolean>>(
    () => currentFlags,
  );

  // Update local flags when dialog opens or current flags change
  useEffect(() => {
    if (open) {
      setLocalFlags(currentFlags);
    }
  }, [open, currentFlags]);

  // Group flags by category
  const flagsByCategory = useMemo((): FeatureFlagsByCategory => {
    const grouped: FeatureFlagsByCategory = {};

    Object.entries(FEATURE_FLAGS).forEach(([key, config]) => {
      const category = config.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }

      grouped[category].push({
        key,
        description: config.description,
        enabled: localFlags[key] ?? config.defaultValue,
      });
    });

    // Sort flags within each category by key
    Object.keys(grouped).forEach((category) => {
      grouped[category].sort((a, b) => a.key.localeCompare(b.key));
    });

    return grouped;
  }, [localFlags]);

  const handleToggleFlag = useCallback((key: string) => {
    setLocalFlags((prev: Record<string, boolean>) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const handleSave = useCallback(() => {
    // Apply all changes
    Object.entries(localFlags).forEach(([key, value]) => {
      setFlag(key, value as boolean);
    });
    onOpenChange(false);
  }, [localFlags, setFlag, onOpenChange]);

  const handleCancel = useCallback(() => {
    // Reset local state to current flags
    setLocalFlags(currentFlags);
    onOpenChange(false);
  }, [currentFlags, onOpenChange]);

  const handleResetAll = useCallback(() => {
    if (
      window.confirm(
        'Are you sure you want to reset all feature flags to their default values?',
      )
    ) {
      resetAllFlags();
      setLocalFlags(currentFlags);
    }
  }, [resetAllFlags, currentFlags]);

  const getCategoryLabel = (category: string): string => {
    return (
      FEATURE_FLAG_CATEGORIES[
        category as keyof typeof FEATURE_FLAG_CATEGORIES
      ] || category
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-hidden p-12">
        <DialogHeader>
          <DialogTitle>Feature Flags (Dev Mode)</DialogTitle>
          <DialogDescription>
            Enable or disable feature flags for development and testing purposes
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-4">
          {Object.entries(flagsByCategory).map(([category, flags]) => (
            <div className="mb-24" key={category}>
              <h3 className="
                mb-12 text-small-brand-medium text-slate
                dark:text-white
              ">
                {getCategoryLabel(category)}
              </h3>

              <div className="space-y-8">
                {flags.map((flag) => (
                  <label
                    className="
                      flex cursor-pointer items-center gap-12 rounded-sm p-8
                      transition-colors
                      hover:bg-light-gray
                      dark:hover:bg-slate-lighten-10
                    "
                    key={flag.key}
                  >
                    <input
                      checked={flag.enabled}
                      className="size-16 cursor-pointer"
                      type="checkbox"
                      onChange={() => handleToggleFlag(flag.key)}
                    />
                    <div className="flex-1">
                      <div className="
                        text-small-brand-medium text-raisin-black
                        dark:text-white
                      ">
                        {flag.key}
                      </div>
                      <div className="
                        text-small-brand-regular text-slate-lighten-5
                        dark:text-slate-lighten-20
                      ">
                        {flag.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex items-center justify-between border-t border-light-gray pt-16">
          <Button size="sm" variant="clear" onClick={handleResetAll}>
            Reset All
          </Button>

          <div className="flex gap-8">
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
